import { useCallback, useEffect, useRef } from "react";

import { reisorProDriverRequest } from "../utils/reisorProMarketHttp.js";

const PUSH_EVENT = "reisor_driver_freight_invite_push";
const ACTION_EVENT = "reisor_driver_freight_invite_action";
const DEFAULT_POLL_MS = 15000;

function clean(value, max = 180) {
  return String(value == null ? "" : value).trim().replace(/\s+/g, " ").slice(0, max);
}

function validInvite(item) {
  const id = clean(item?.id, 80);
  const freightId = clean(item?.marketFreightId, 180);
  const status = clean(item?.status, 20).toUpperCase();
  return /^dfi_[a-f0-9]{40}$/.test(id) && Boolean(freightId) && status === "SENT";
}

export function useDriverFreightInviteInbox({
  enabled = true,
  token = "",
  onInvite,
} = {}) {
  const busyRef = useRef(false);
  const activeInviteRef = useRef("");
  const onInviteRef = useRef(onInvite);

  useEffect(() => {
    onInviteRef.current = onInvite;
  }, [onInvite]);

  useEffect(() => {
    activeInviteRef.current = "";
  }, [token]);

  const markOpened = useCallback(async (inviteId) => {
    const id = clean(inviteId, 80);
    if (!/^dfi_[a-f0-9]{40}$/.test(id) || !token) return false;

    if (activeInviteRef.current === id) activeInviteRef.current = "";

    try {
      const { response, data } = await reisorProDriverRequest(
        `/api/vehicle-market/driver/invites/${encodeURIComponent(id)}/opened`,
        { token, method: "POST" }
      );
      return Boolean(response?.ok && data?.ok);
    } catch {
      return false;
    }
  }, [token]);

  const sync = useCallback(async () => {
    if (!enabled || !token || busyRef.current || activeInviteRef.current) return false;
    busyRef.current = true;

    try {
      const { response, data } = await reisorProDriverRequest(
        "/api/vehicle-market/driver/invites?limit=20",
        { token }
      );
      if (!response?.ok || !data?.ok) return false;

      const items = Array.isArray(data?.items) ? data.items : [];
      const next = items.find(validInvite);
      if (!next) return false;

      activeInviteRef.current = clean(next.id, 80);
      try {
        onInviteRef.current?.(next);
      } catch {
        activeInviteRef.current = "";
        return false;
      }
      return true;
    } catch {
      return false;
    } finally {
      busyRef.current = false;
    }
  }, [enabled, token]);

  useEffect(() => {
    if (!enabled || !token) return undefined;

    const onFocus = () => { sync().catch(() => {}); };
    const onVisible = () => {
      if (document.visibilityState === "visible") sync().catch(() => {});
    };
    const onPush = () => { sync().catch(() => {}); };
    const onAction = (event) => {
      const inviteId = clean(event?.detail?.inviteId, 80);
      if (inviteId) markOpened(inviteId).catch(() => {});
    };

    sync().catch(() => {});
    const timer = window.setInterval(() => { sync().catch(() => {}); }, DEFAULT_POLL_MS);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(PUSH_EVENT, onPush);
    window.addEventListener(ACTION_EVENT, onAction);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(PUSH_EVENT, onPush);
      window.removeEventListener(ACTION_EVENT, onAction);
    };
  }, [enabled, token, sync, markOpened]);

  return {
    refresh: sync,
    markOpened,
  };
}
